import { Link, useSearch } from "@tanstack/react-router";
import { AlertCircle, ExternalLink } from "lucide-react";

import { SectionDivider } from "@/components/cinch/SectionDivider";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

import {
  useReconciliation,
  type ReconcileCinchOnly,
  type ReconcileHubspotOnly,
  type ReconcileMismatch,
  type ReconcileMismatchContract,
} from "../api";
import { CroForecastFilterBar } from "../components/FilterBar";
import { CroForecastSubNav } from "../components/SubNav";
import { hubspotDealUrl } from "../constants";
import { fmtCentsAccounting, fmtCentsWhole, fmtMonthName } from "../format";
import { resolveSearch, type CroForecastSearch } from "../search";

/**
 * Reconcile Deals tab — gated on cro_forecast.write.
 *
 * Three sections, all scoped to a single fiscal month (derived from the
 * filter-bar ISO-week's Monday):
 *
 * 1. Closed-won in HubSpot, missing in Cinch billing_contracts.
 * 2. Signed in Cinch billing_contracts, no matching HubSpot deal id
 *    (or the deal id doesn't appear as closed-won in this window).
 * 3. Both sides matched, but SUM of annualized contract values for the
 *    HubSpot deal differs from HubSpot's deal amount beyond tolerance.
 *    A single deal can fan out to multiple Cinch contracts (the
 *    Reseller-Deals split is the common case), so we aggregate by deal
 *    before computing the delta — one row per HubSpot deal, never one
 *    row per contract.
 */
export function CroForecastReconcileDealsPage() {
  const search = useSearch({
    from: "/cro-forecast/reconcile-deals",
  }) as CroForecastSearch;
  const { year, week } = resolveSearch(search);
  // Default the month filter to the iso-week's Monday's month — matches
  // how the rest of the feature derives "fiscal month" from the URL.
  const weekStart = isoWeekMonday(year, week);
  const fiscalYear = weekStart.getFullYear();
  const fiscalMonth = weekStart.getMonth() + 1;

  const recon = useReconciliation(fiscalYear, fiscalMonth);

  const hub = recon.data?.unmatched_hubspot ?? [];
  const cinch = recon.data?.unmatched_cinch ?? [];
  const mismatch = recon.data?.mismatched_amounts ?? [];

  return (
    <div className="flex flex-col">
      <CroForecastFilterBar search={search} />
      <CroForecastSubNav search={search} />

      <div className="flex flex-col gap-6 p-4 md:p-6">
        <header>
          <h2 className="text-lg font-semibold">
            Reconcile Deals — {fmtMonthName(fiscalMonth)} {fiscalYear}
          </h2>
          <p className="text-sm text-muted-foreground">
            HubSpot closed-won deals (by <code>closed_at</code>) vs Cinch
            billing contracts (by <code>contract_signed_at</code>) for the
            filtered month. A single HubSpot deal can have multiple Cinch
            contracts (e.g. Reseller Deals split into a New Contract for
            the end customer + a Purchase Order for the reseller); the
            mismatch row sums those before comparing.
          </p>
        </header>

        {recon.isLoading && (
          <p className="text-sm text-muted-foreground">Loading…</p>
        )}
        {recon.error && (
          <Card>
            <CardContent className="pt-6">
              <p className="flex items-center gap-2 text-sm text-destructive">
                <AlertCircle className="h-4 w-4" />
                Failed to load reconciliation: {(recon.error as Error).message}
              </p>
            </CardContent>
          </Card>
        )}

        {recon.data && (
          <>
            <SectionDivider
              label={`Closed-won in HubSpot, missing in Cinch (${hub.length})`}
              description="Deal closed-won in HubSpot — no contract row in Cinch's billing_contracts for the deal id"
            />
            <HubspotOnlyCard rows={hub} />

            <SectionDivider
              label={`Signed in Cinch, missing in HubSpot (${cinch.length})`}
              description="Click a row to open the contract drawer; click the contract ID to open the expanded view in the Contracts feature."
            />
            <CinchOnlyCard rows={cinch} />

            <SectionDivider
              label={`Amount mismatches (${mismatch.length})`}
              description="Both systems have the deal — sum of annualized contract values disagrees with HubSpot's deal amount by more than $1."
            />
            <MismatchCard rows={mismatch} />
          </>
        )}
      </div>
    </div>
  );
}

// ── unmatched-HubSpot table ──────────────────────────────────────────────

function HubspotOnlyCard({ rows }: { rows: ReconcileHubspotOnly[] }) {
  if (rows.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">
            All HubSpot closed-won deals have a matching Cinch contract. ✓
          </p>
        </CardContent>
      </Card>
    );
  }
  const total = rows.reduce((acc, r) => acc + (r.amount_cents ?? 0), 0);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between text-sm">
          <span>HubSpot closed-won without a Cinch contract</span>
          <span className="font-mono text-muted-foreground">
            {rows.length} deals · {fmtCentsWhole(total)}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Deal</TableHead>
              <TableHead>Pipeline / Stage</TableHead>
              <TableHead>Owner</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Closed</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.hubspot_deal_id}>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {r.pipeline_name ?? "—"}
                  {r.stage_name ? ` · ${r.stage_name}` : ""}
                </TableCell>
                <TableCell>{r.owner_display_name ?? "—"}</TableCell>
                <TableCell className="text-right font-mono">
                  {fmtCentsWhole(r.amount_cents)}
                </TableCell>
                <TableCell className="text-xs">
                  {fmtDate(r.closed_at)}
                </TableCell>
                <TableCell>
                  <a
                    href={hubspotDealUrl(r.hubspot_deal_id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-foreground"
                    aria-label="Open in HubSpot"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// ── unmatched-Cinch table (clickable rows → contracts drawer/expanded) ───

function CinchOnlyCard({ rows }: { rows: ReconcileCinchOnly[] }) {
  if (rows.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">
            All Cinch contracts this month have a matching HubSpot closed-won
            deal. ✓
          </p>
        </CardContent>
      </Card>
    );
  }
  const total = rows.reduce((acc, r) => acc + (r.revenue_cents ?? 0), 0);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between text-sm">
          <span>Cinch contracts without a HubSpot closed-won deal</span>
          <span className="font-mono text-muted-foreground">
            {rows.length} contracts · {fmtCentsWhole(total)}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Contract ID (expanded)</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>HubSpot Deal ID on contract</TableHead>
              <TableHead className="text-right">Annualized $</TableHead>
              <TableHead>Signed</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <ContractRow key={r.contract_id} row={r} />
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function ContractRow({ row }: { row: ReconcileCinchOnly }) {
  // Row cells (other than the ID) → quick drawer.
  // Contract-ID cell → expanded view (per user spec: "click the record →
  // drawer; click the id → expanded"). Both use TanStack Link so the
  // back button steps through history (mirrors how the Contracts page
  // itself navigates).
  return (
    <TableRow className="cursor-pointer hover:bg-muted/30">
      <TableCell className="p-0">
        <ContractExpandedLink
          contractId={row.contract_id}
          className="block p-2 font-mono text-xs underline-offset-2 hover:underline"
        >
          {row.contract_id.slice(0, 8)}…
        </ContractExpandedLink>
      </TableCell>
      <TableCell className="p-0">
        <ContractDrawerLink contractId={row.contract_id} className="block p-2">
          {row.contract_type ?? "—"}
        </ContractDrawerLink>
      </TableCell>
      <TableCell className="font-mono text-xs">
        {row.hubspot_deal_id_on_contract ? (
          <a
            href={hubspotDealUrl(row.hubspot_deal_id_on_contract)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 underline-offset-2 hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {row.hubspot_deal_id_on_contract}
            <ExternalLink className="h-3 w-3" />
          </a>
        ) : (
          <span className="text-destructive">— null —</span>
        )}
      </TableCell>
      <TableCell className="p-0 text-right">
        <ContractDrawerLink
          contractId={row.contract_id}
          className="block p-2 text-right font-mono"
        >
          {fmtCentsWhole(row.revenue_cents)}
        </ContractDrawerLink>
      </TableCell>
      <TableCell className="p-0">
        <ContractDrawerLink contractId={row.contract_id} className="block p-2 text-xs">
          {fmtDate(row.signed_at)}
        </ContractDrawerLink>
      </TableCell>
    </TableRow>
  );
}

// ── mismatch table — one row per HubSpot deal, contracts listed inline ───

function MismatchCard({ rows }: { rows: ReconcileMismatch[] }) {
  if (rows.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">
            No amount mismatches this month. ✓
          </p>
        </CardContent>
      </Card>
    );
  }
  const totalDelta = rows.reduce((acc, r) => acc + (r.delta_cents ?? 0), 0);
  // Sort by absolute delta descending — biggest drift first.
  const sorted = [...rows].sort(
    (a, b) => Math.abs(b.delta_cents) - Math.abs(a.delta_cents),
  );
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between text-sm">
          <span>HubSpot amount vs Cinch annualized (sum) — disagree</span>
          <span className="font-mono text-muted-foreground">
            {rows.length} deals · net Δ {fmtCentsAccounting(totalDelta)}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Deal</TableHead>
              <TableHead>Owner</TableHead>
              <TableHead className="text-right">HubSpot $</TableHead>
              <TableHead className="text-right">Cinch annualized</TableHead>
              <TableHead className="text-right">Δ (Cinch − HubSpot)</TableHead>
              <TableHead>Cinch contracts</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((r) => (
              <TableRow key={r.hubspot_deal_id}>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell>{r.owner_display_name ?? "—"}</TableCell>
                <TableCell className="text-right font-mono">
                  {fmtCentsWhole(r.deal_amount_cents)}
                </TableCell>
                <TableCell className="text-right font-mono">
                  {fmtCentsWhole(r.contract_revenue_cents)}
                </TableCell>
                <TableCell
                  className={cn(
                    "text-right font-mono",
                    r.delta_cents < 0 ? "text-destructive" : "text-power",
                  )}
                >
                  {fmtCentsAccounting(r.delta_cents)}
                </TableCell>
                <TableCell>
                  <ContractList contracts={r.contracts} />
                </TableCell>
                <TableCell>
                  <a
                    href={hubspotDealUrl(r.hubspot_deal_id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-foreground"
                    aria-label="Open deal in HubSpot"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function ContractList({ contracts }: { contracts: ReconcileMismatchContract[] }) {
  // Same drill-through semantics as the unmatched-Cinch table: ID →
  // expanded view; the row "background" (the type) → quick drawer.
  return (
    <div className="flex flex-col gap-0.5 text-xs">
      {contracts.map((c) => (
        <div key={c.contract_id} className="flex items-center gap-2">
          <ContractExpandedLink
            contractId={c.contract_id}
            className="font-mono underline-offset-2 hover:underline"
          >
            {c.contract_id.slice(0, 8)}…
          </ContractExpandedLink>
          <ContractDrawerLink contractId={c.contract_id}>
            <span className="text-muted-foreground hover:text-foreground">
              {c.contract_type ?? "—"}
            </span>
          </ContractDrawerLink>
          <span className="ml-auto font-mono text-muted-foreground">
            {fmtCentsWhole(c.revenue_cents)}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── helpers ──────────────────────────────────────────────────────────────

/** Deep-link into the Contracts feature's quick drawer for a contract.
 *  The Contracts page reads `?tab=contracts&detail=contracts:<id>` from
 *  the URL and renders the same RecordDrawer used everywhere else, so
 *  there is no duplicated UI to maintain. */
function ContractDrawerLink({
  contractId,
  className,
  children,
}: {
  contractId: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <Link
      to="/contracts"
      search={{
        tab: "contracts",
        detail: `contracts:${contractId}`,
      }}
      className={className}
    >
      {children}
    </Link>
  );
}

/** Deep-link into the Contracts feature's expanded view for a contract.
 *  Same target URL as the drawer, plus `expanded=true`. */
function ContractExpandedLink({
  contractId,
  className,
  children,
  ...rest
}: {
  contractId: string;
  className?: string;
  children: React.ReactNode;
} & React.HTMLAttributes<HTMLAnchorElement>) {
  return (
    <Link
      to="/contracts"
      search={{
        tab: "contracts",
        detail: `contracts:${contractId}`,
        expanded: true,
      }}
      className={cn(
        "text-muted-foreground hover:text-foreground",
        className,
      )}
      onClick={(e) => e.stopPropagation()}
      {...rest}
    >
      {children}
    </Link>
  );
}

function isoWeekMonday(year: number, week: number): Date {
  // Mirror Python's date.fromisocalendar(year, week, 1): ISO week 1 contains
  // Jan 4. JS lacks a direct equivalent, so do it manually.
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Dow = jan4.getUTCDay() || 7; // 1=Mon..7=Sun
  const isoMonday1 = new Date(jan4);
  isoMonday1.setUTCDate(jan4.getUTCDate() - (jan4Dow - 1));
  const target = new Date(isoMonday1);
  target.setUTCDate(isoMonday1.getUTCDate() + (week - 1) * 7);
  return new Date(target.getUTCFullYear(), target.getUTCMonth(), target.getUTCDate());
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}
